import type {
  RhPresenceType,
  UserRhsType,
  UserColaboratorAssignmentType,
  UsersActivityType
} from '#shared/types/dbTypes/rhpresencesTypes'
import type {
  HoursDayEditState,
  HoursDayEditForm
} from '#shared/types/appTypes/responseDbTypes/homeTypes'
import type { AppHollydayType } from '#shared/types/appTypes/responseDbTypes/homeTypes'
import type { MonthType } from '#shared/types/appTypes/globalTypes'
import type { DateValue } from '@internationalized/date'
import type {
  CenterHours,
  DailyRecordResource,
  EditDayPresence,
  CenterHoursData,
  ResourceFormData
} from '#shared/types/appTypes/rhPresencesTable/homeTable'
import { copy } from 'fast-copy'
import { useLoginStore } from '~/stores/LoginStore'
import {
  getCenterHours,
  checkIfDayMatchesFilter,
  getMatchedCenterCodes
} from '~/utils/presencesFilters'
import { useGlobalStore } from '~/stores/GlobalStore'

const userData = useLoginStore()

export const usePresencesStore = defineStore('PresencesStore', () => {
  const globalStore = useGlobalStore()

  //SECTION - STATE
  const dbPresences: Ref<RhPresenceType[] | null> = ref(null)
  const dbUsersColaborators: Ref<UserRhsType[] | null> = ref(null)
  const dbUsersActivities: Ref<UsersActivityType[] | null> = ref(null)
  const filterByUserColaborators: Ref<boolean> = ref(false)
  const filterByCenters: Ref<string[]> = ref([])
  const filterByIssue: Ref<string | null> = ref(null)
  const filterOnlyFaults: Ref<boolean> = ref(false)
  const filterByRhSearch: Ref<string | null> = ref(null)
  const firstDayOfWorkPeriod: Ref<string | null> = ref(null)
  const holidays: Ref<AppHollydayType[] | null> = ref(null)
  const lastDayOfWorkPeriod: Ref<string | null> = ref(null)
  const modalEditHoursState: Ref<HoursDayEditState | null> = ref(null)
  const modalEditHoursPayload: Ref<{ 'rowIndex': number; 'dayIndex': number } | null> = ref(null)
  const hoursDayEditFormState = reactive<HoursDayEditForm>({
    'hours': [],
    'fault': undefined
  })
  const modalEditHoursIsOpen: Ref<boolean> = ref(false)
  const modalNotOwnerIsOpen: Ref<boolean> = ref(false)
  const modalNotOwnerState: Ref<{
    'centersHours': CenterHours[]
    'owners': string[]
    'rhId': string
    'rhName': string
  } | null> = ref(null)
  const openMonth: Ref<MonthType | undefined> = ref(undefined)
  const hasNoColaborators: Ref<boolean> = ref(false)
  const hasNoPresences: Ref<boolean> = ref(false)

  // Daily Record state
  const dailyRecordColaboratorData: Ref<DailyRecordResource[]> = ref([])
  const dailyRecordDefaultCenter: Ref<string | undefined> = ref(undefined)
  const dailyRecordDefaultActivity: Ref<string> = ref('')
  const dailyRecordFilledDates: Ref<string[]> = ref([])
  const dailyRecordIsBulkEditing: Ref<boolean> = ref(false)
  const dailyRecordBulkFormState = reactive<{ 'centers': CenterHoursData[]; 'fault'?: string }>({
    'centers': []
  })
  const dailyRecordFormState = reactive<{ 'resources': ResourceFormData[] }>({ 'resources': [] })
  const dailyRecordSelectedResourceIndex: Ref<number | null> = ref(null)
  const dailyRecordShowForm: Ref<boolean> = ref(false)
  const dailyRecordTargetDate: Ref<DateValue | null> = ref(null)
  const dailyRecordValidatedResources: Ref<Set<string>> = ref(new Set())
  const dailyRecordValidationPending: Ref<boolean> = ref(false)
  const dailyRecordFilteredResourceIndexes: Ref<number[]> = ref([])

  // Edit Daily Record state
  const editDailyRecordDayData: Ref<EditDayPresence[] | null> = ref(null)
  const editDailyRecordSelectedDate: Ref<DateValue | null> = ref(null)

  // Historic query state
  const historicQuery = reactive<{ 'year': string | null; 'month': string | null }>({
    'year': null,
    'month': null
  })

  // Editable presences state (shared between home.vue and editDailyRecord.vue)
  const editablePresencesDates: Ref<string[]> = ref([])
  const hasEditablePresences: Ref<boolean> = ref(false)
  //!SECTION - END STATE

  //SECTION - GETTERS
  const completePresences = computed(() => {
    const copyDbPresences = copy(dbPresences.value)

    const findedUserColaborators = dbUsersColaborators.value?.find(
      (user: UserRhsType) => user.email === userData.userEmail
    )
    if (!findedUserColaborators) return []
    const copyDbUserColaborators = copy(findedUserColaborators.userColaboratorsArray)
    const colaboratorIds = copyDbUserColaborators.map((a: any) => a.rhId || a)
    const copyLastDayOfWorkPeriod = copy(lastDayOfWorkPeriod.value)
    const copyFirstDayOfTheMonth = copy(firstDayOfWorkPeriod.value)
    const copyHolidays = copy(holidays.value)
    if (!copyDbPresences || !copyDbUserColaborators || !copyHolidays) return []
    return getCompletePresencesTable(
      copyDbPresences,
      colaboratorIds,
      copyLastDayOfWorkPeriod,
      copyFirstDayOfTheMonth,
      copyHolidays,
      globalStore.rhArray || []
    ).homeTableArray
  })

  const currentUserAssignments = computed((): UserColaboratorAssignmentType[] => {
    const userColaboratorsRecord = dbUsersColaborators.value?.find(
      (user: UserRhsType) => user.email === userData.userEmail
    )
    return userColaboratorsRecord?.userColaboratorsArray ?? []
  })

  const filteredPresences = computed(() => {
    const data = completePresences.value
    if (!data || data.length === 0) return []

    let filtered = data

    // Apply row-level filters (hide entire rows)
    if (filterByUserColaborators.value) {
      filtered = filtered.filter(row => row.isUserColaborator === true)
    }

    if (filterOnlyFaults.value) {
      filtered = filtered.filter(row => row.days.some(day => day.faults && day.faults !== 'P'))
    }

    // Apply search filter
    if (filterByRhSearch.value) {
      const searchTerm = filterByRhSearch.value.toLowerCase()
      filtered = filtered.filter(
        row =>
          row.rhId.toLowerCase().includes(searchTerm) ||
          row.rhName.toLowerCase().includes(searchTerm) ||
          row.rhProfession.toLowerCase().includes(searchTerm)
      )
    }

    // Hide rows without any hours in the selected centers
    if (filterByCenters.value.length > 0) {
      filtered = filtered.filter(row =>
        row.days.some(day => day.centersHours.some(ch => filterByCenters.value.includes(ch.code)))
      )
    }

    // Apply column-level filters (mark matching days)
    if (filterByCenters.value.length > 0 || filterByIssue.value) {
      filtered = filtered.map(row => ({
        ...row,
        'days': row.days.map(day => ({
          ...day,
          'matchesFilter': checkIfDayMatchesFilter(day, filterByCenters.value, filterByIssue.value)
        }))
      }))
    }

    // Add display hours based on center filter
    if (filterByCenters.value.length > 0) {
      filtered = filtered.map(row => ({
        ...row,
        'days': row.days.map(day => {
          const centerHours = getCenterHours(day, filterByCenters.value)
          return {
            ...day,
            'displayUsualHours': centerHours.usualHours,
            'displayExtraHours': centerHours.extraHours,
            'matchedCenterCodes': getMatchedCenterCodes(day, filterByCenters.value)
          }
        })
      }))
    } else {
      // When no center filter, display total hours
      filtered = filtered.map(row => ({
        ...row,
        'days': row.days.map(day => ({
          ...day,
          'displayUsualHours': typeof day.usualHours === 'number' ? day.usualHours : 0,
          'displayExtraHours': typeof day.extraHours === 'number' ? day.extraHours : 0
        }))
      }))
    }

    return filtered
  })

  const modifiedRecords = computed((): RhPresenceType[] => {
    if (!dbPresences.value || !dailyRecordFormState.resources.length) return []

    const modifiedRecords: RhPresenceType[] = []

    // Find original presence from dbPresences
    const targetDate = `${dailyRecordTargetDate.value?.year}-${String(dailyRecordTargetDate.value?.month).padStart(2, '0')}-${String(dailyRecordTargetDate.value?.day).padStart(2, '0')}`

    // Check all resources, not just the selected one
    for (let i = 0; i < dailyRecordFormState.resources.length; i++) {
      const resource = dailyRecordFormState.resources[i]
      if (!resource) continue

      const originalPresence = dbPresences.value!.find(
        presence => presence.rhId === resource.rhId && presence.date === targetDate
      )

      if (!originalPresence) continue // Skip if no original record

      // Convert form state to RhPresenceType format for comparison
      const currentPresence: RhPresenceType = {
        _id: originalPresence._id,
        createdAt: originalPresence.createdAt,
        createdBy: originalPresence.createdBy,
        date: targetDate,
        fault: resource.fault,
        hours: [],
        rhId: resource.rhId,
        updatedAt: new Date().toISOString(),
        updatedBy: userData.userEmail || 'unknown',
        uniqueCode: originalPresence.uniqueCode
      }

      // Convert centers array using for loop
      for (let j = 0; j < resource.centers.length; j++) {
        const center = resource.centers[j]
        if (center && center.centerId) {
          currentPresence.hours.push({
            activities: center.activities,
            center: center.centerId
          })
        }
      }

      // Compare current state with original
      const hasChanges = JSON.stringify(currentPresence) !== JSON.stringify(originalPresence)
      if (hasChanges) {
        // Check if item with uniqueCode already exists and replace, otherwise push
        const existingIndex = modifiedRecords.findIndex(
          record => record.uniqueCode === currentPresence.uniqueCode
        )
        if (existingIndex >= 0) {
          modifiedRecords[existingIndex] = currentPresence // Replace existing item
        } else {
          modifiedRecords.push(currentPresence) // Add new item
        }
      }
    }

    return modifiedRecords
  })

  //!SECTION - END GETTERS

  //SECTION - ACTIONS
  // Function to check if a date is a holiday
  function isDateHoliday(dateValue: DateValue | null): boolean {
    if (!dateValue || !holidays.value) return false
    return holidays.value.some(h => h.month === dateValue.month && h.day === dateValue.day)
  }

  function resetValidationState(): void {
    dailyRecordValidatedResources.value = new Set()
    dailyRecordValidationPending.value = false
    dailyRecordFilteredResourceIndexes.value = []
  }

  function validateResource(resourceIndex: number): void {
    if (!dailyRecordTargetDate.value) return
    const resource = dailyRecordFormState.resources[resourceIndex]
    if (!resource) return

    const targetDateStr = `${dailyRecordTargetDate.value.year}-${String(dailyRecordTargetDate.value.month).padStart(2, '0')}-${String(dailyRecordTargetDate.value.day).padStart(2, '0')}`
    const validationKey = `${resource.rhId}-${targetDateStr}`
    dailyRecordValidatedResources.value.add(validationKey)
    dailyRecordValidationPending.value = false
  }

  function unvalidateResource(resourceIndex: number): void {
    if (!dailyRecordTargetDate.value) return
    const resource = dailyRecordFormState.resources[resourceIndex]
    if (!resource) return

    const targetDateStr = `${dailyRecordTargetDate.value.year}-${String(dailyRecordTargetDate.value.month).padStart(2, '0')}-${String(dailyRecordTargetDate.value.day).padStart(2, '0')}`
    const validationKey = `${resource.rhId}-${targetDateStr}`
    dailyRecordValidatedResources.value.delete(validationKey)
  }

  function validateAllResources(): void {
    if (!dailyRecordTargetDate.value) return
    const targetDateStr = `${dailyRecordTargetDate.value.year}-${String(dailyRecordTargetDate.value.month).padStart(2, '0')}-${String(dailyRecordTargetDate.value.day).padStart(2, '0')}`

    for (let i = 0; i < dailyRecordFormState.resources.length; i++) {
      const resource = dailyRecordFormState.resources[i]
      if (!resource) continue
      const validationKey = `${resource.rhId}-${targetDateStr}`
      dailyRecordValidatedResources.value.add(validationKey)
    }
    dailyRecordValidationPending.value = false
  }

  function resetDailyRecordState(): void {
    dailyRecordColaboratorData.value = []
    dailyRecordDefaultCenter.value = undefined
    dailyRecordDefaultActivity.value = ''
    dailyRecordFilledDates.value = []
    dailyRecordIsBulkEditing.value = false
    dailyRecordBulkFormState.centers = []
    dailyRecordFormState.resources = []
    dailyRecordSelectedResourceIndex.value = null
    dailyRecordShowForm.value = false
    dailyRecordTargetDate.value = null
    resetValidationState()
    // Reset edit daily record state
    editDailyRecordDayData.value = null
    editDailyRecordSelectedDate.value = null
    // NOTE: Do NOT reset editablePresencesDates and hasEditablePresences here
    // They are fetched once in home.vue and should persist across navigation
  }

  function resetPresencesUiState(): void {
    modalEditHoursIsOpen.value = false
    modalEditHoursState.value = null
    modalEditHoursPayload.value = null
    modalNotOwnerIsOpen.value = false
    modalNotOwnerState.value = null
    filterByUserColaborators.value = false
    filterByCenters.value = []
    filterByIssue.value = null
    filterOnlyFaults.value = false
    filterByRhSearch.value = null
  }
  //!SECTION - END ACTIONS

  //SECTION - RETURN
  return {
    completePresences,
    currentUserAssignments,
    dailyRecordBulkFormState,
    dailyRecordColaboratorData,
    dailyRecordDefaultCenter,
    dailyRecordDefaultActivity,
    dailyRecordFilledDates,
    dailyRecordFilteredResourceIndexes,
    dailyRecordFormState,
    dailyRecordIsBulkEditing,
    dailyRecordSelectedResourceIndex,
    dailyRecordShowForm,
    dailyRecordTargetDate,
    dailyRecordValidatedResources,
    dailyRecordValidationPending,
    dbPresences,
    dbUsersActivities,
    dbUsersColaborators,
    editablePresencesDates,
    editDailyRecordDayData,
    editDailyRecordSelectedDate,
    filterByCenters,
    filterByIssue,
    filterByRhSearch,
    filterByUserColaborators,
    filterOnlyFaults,
    filteredPresences,
    firstDayOfWorkPeriod,
    hasEditablePresences,
    hasNoColaborators,
    hasNoPresences,
    historicQuery,
    holidays,
    hoursDayEditFormState,
    isDateHoliday,
    lastDayOfWorkPeriod,
    modalEditHoursIsOpen,
    modalEditHoursState,
    modalEditHoursPayload,
    modalNotOwnerIsOpen,
    modalNotOwnerState,
    modifiedRecords,
    openMonth,
    resetDailyRecordState,
    resetPresencesUiState,
    resetValidationState,
    unvalidateResource,
    validateAllResources,
    validateResource
  }
  //!SECTION - END RETURN
})
